// Client services: Personal Credit Building (seven-phase Prime Circle roadmap) and Amex Global Transfer
// (six-phase roadmap). The two are separate operational categories: a client holding both has two separate
// enrollments, each with its own phases, checklists, tasks and reporting.

export const CLIENT_SERVICE_TYPES = ['credit_building', 'global_transfer'] as const;
export type ClientServiceType = (typeof CLIENT_SERVICE_TYPES)[number];
export const CLIENT_SERVICE_LABELS: Record<ClientServiceType, string> = {
  credit_building: 'Personal Credit Building',
  global_transfer: 'Amex Global Transfer',
};

export const ENROLLMENT_STATUSES = ['active', 'paused', 'completed', 'cancelled'] as const;
export type EnrollmentStatus = (typeof ENROLLMENT_STATUSES)[number];

/** `waiting_institution` = ordinary bank / IRS / state / bureau processing time: never counted as staff delay. */
export const PHASE_STATUSES = ['not_started', 'in_progress', 'blocked', 'waiting_institution', 'completed', 'skipped'] as const;
export type PhaseStatus = (typeof PHASE_STATUSES)[number];
export const PHASE_STATUS_LABELS: Record<PhaseStatus, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  blocked: 'Blocked',
  waiting_institution: 'Waiting on institution',
  completed: 'Completed',
  skipped: 'Not applicable',
};

export const STEP_STATUSES = ['pending', 'in_progress', 'done', 'blocked', 'n_a'] as const;
export type StepStatus = (typeof STEP_STATUSES)[number];

// ---------- LLC / EIN / ITIN ----------
export const LLC_STATUSES = [
  'information_collection',
  'name_review',
  'documents_preparing',
  'filing_ready',
  'filed_with_state',
  'state_processing',
  'formation_approved',
  'ein_pending',
  'ein_issued',
  'documents_completed',
  'completed',
  'blocked',
] as const;
export type LlcStatus = (typeof LLC_STATUSES)[number];
export const LLC_STATUS_LABELS: Record<LlcStatus, string> = {
  information_collection: 'Information Collection',
  name_review: 'Name Review',
  documents_preparing: 'Documents Preparing',
  filing_ready: 'Filing Ready',
  filed_with_state: 'Filed with State',
  state_processing: 'State Processing',
  formation_approved: 'Formation Approved',
  ein_pending: 'EIN Pending',
  ein_issued: 'EIN Issued',
  documents_completed: 'Documents Completed',
  completed: 'Completed',
  blocked: 'Blocked',
};

/** EIN is tracked as its own milestone (different timeline from the state formation). */
export const EIN_STATUSES = ['not_started', 'pending', 'issued', 'rejected'] as const;
export type EinStatus = (typeof EIN_STATUSES)[number];
export const EIN_STATUS_LABELS: Record<EinStatus, string> = { not_started: 'Not started', pending: 'Application pending', issued: 'Issued', rejected: 'Rejected' };

/** Simple document/milestone status used for Operating Agreement, formation documents and D-U-N-S. */
export const MILESTONE_STATUSES = ['not_started', 'in_progress', 'completed', 'not_applicable'] as const;
export type MilestoneStatus = (typeof MILESTONE_STATUSES)[number];

export const ITIN_STATUSES = [
  'not_started',
  'documents_requested',
  'documents_received',
  'application_preparing',
  'ready_for_submission',
  'submitted',
  'irs_processing',
  'additional_information_required',
  'issued',
  'rejected',
  'completed',
] as const;
export type ItinStatus = (typeof ITIN_STATUSES)[number];
export const ITIN_STATUS_LABELS: Record<ItinStatus, string> = {
  not_started: 'Not Started',
  documents_requested: 'Documents Requested',
  documents_received: 'Documents Received',
  application_preparing: 'Application Preparing',
  ready_for_submission: 'Ready for Submission',
  submitted: 'Submitted',
  irs_processing: 'IRS Processing',
  additional_information_required: 'Additional Information Required',
  issued: 'Approved / Issued',
  rejected: 'Rejected',
  completed: 'Completed',
};

// ---------- Client documents ----------
export const DOCUMENT_STATUSES = ['requested', 'uploaded', 'approved', 'rejected', 'expired'] as const;
export type DocumentStatus = (typeof DOCUMENT_STATUSES)[number];

export interface DocumentType {
  label: string;
  /** Services that normally need it (requested automatically on enrollment when `auto`). */
  services: ClientServiceType[];
  /** Part of the credit-bureau ID pack. */
  idPack?: boolean;
  /** Requested automatically when the enrollment starts (only for the listed services / flags). */
  auto?: boolean;
  /** Only when the enrollment includes this optional milestone. */
  requiresFlag?: 'includesLlc' | 'includesEin' | 'includesItin';
}

export const DOCUMENT_TYPES = {
  gov_id_front: { label: 'Government ID — front', services: ['credit_building', 'global_transfer'], idPack: true, auto: true },
  gov_id_back: { label: 'Government ID — back', services: ['credit_building', 'global_transfer'], idPack: true, auto: true },
  ssn_or_itin_letter: { label: 'SSN card or ITIN letter', services: ['credit_building'], idPack: true, auto: true },
  proof_of_address_1: { label: 'Proof of address (utility bill or bank statement)', services: ['credit_building'], idPack: true, auto: true },
  proof_of_address_2: { label: 'Second proof of address', services: ['credit_building'], idPack: true, auto: true },
  passport: { label: 'Passport (photo page)', services: ['credit_building', 'global_transfer'], auto: false },
  foreign_amex_statement: { label: 'Foreign Amex card statement', services: ['global_transfer'], auto: true },
  foreign_amex_card: { label: 'Foreign Amex card (front, number masked)', services: ['global_transfer'], auto: true },
  us_address_proof: { label: 'US address evidence', services: ['global_transfer'], auto: true },
  w7: { label: 'Form W-7 (signed)', services: ['credit_building'], auto: true, requiresFlag: 'includesItin' },
  itin_supporting: { label: 'ITIN supporting documents', services: ['credit_building'], auto: true, requiresFlag: 'includesItin' },
  llc_articles: { label: 'LLC articles of organization', services: ['credit_building'], requiresFlag: 'includesLlc' },
  operating_agreement: { label: 'Operating agreement', services: ['credit_building'], requiresFlag: 'includesLlc' },
  ein_letter: { label: 'EIN confirmation letter (CP 575)', services: ['credit_building'], requiresFlag: 'includesEin' },
  itin_letter: { label: 'ITIN assignment letter (CP 565)', services: ['credit_building'], requiresFlag: 'includesItin' },
  credit_report: { label: 'Credit report', services: ['credit_building'] },
  bank_statement: { label: 'Bank statement', services: ['credit_building', 'global_transfer'] },
  idpack_authorization: { label: 'Client authorization to mail ID documents', services: ['credit_building'] },
  other: { label: 'Other document', services: ['credit_building', 'global_transfer'] },
} as const satisfies Record<string, DocumentType>;
export type DocumentKey = keyof typeof DOCUMENT_TYPES;
export const DOCUMENT_KEYS = Object.keys(DOCUMENT_TYPES) as DocumentKey[];
export const ID_PACK_DOCUMENTS = DOCUMENT_KEYS.filter((k) => (DOCUMENT_TYPES[k] as DocumentType).idPack);

// ---------- ID packs & bureau mailings ----------
export const ID_PACK_STATUSES = ['draft', 'awaiting_authorization', 'ready', 'submitting', 'submitted', 'partially_failed', 'completed', 'cancelled'] as const;
export type IdPackStatus = (typeof ID_PACK_STATUSES)[number];

export const CREDIT_BUREAUS = ['experian', 'equifax', 'transunion'] as const;
export type CreditBureau = (typeof CREDIT_BUREAUS)[number];
export const CREDIT_BUREAU_LABELS: Record<CreditBureau, string> = { experian: 'Experian', equifax: 'Equifax', transunion: 'TransUnion' };

export const BUREAU_MAILING_STATUSES = ['pending', 'submitted', 'accepted', 'mailed', 'in_transit', 'delivered', 'returned', 'failed', 'cancelled'] as const;
export type BureauMailingStatus = (typeof BUREAU_MAILING_STATUSES)[number];
export const MAILING_PROVIDERS = ['letterstream', 'manual'] as const;

// ---------- Operational payment requests ----------
export const PAYMENT_REQUEST_KINDS = ['standard', 'operational'] as const;
export type PaymentRequestKind = (typeof PAYMENT_REQUEST_KINDS)[number];
export const OPS_EXPENSE_CATEGORIES = ['id_pack_mailing', 'letterstream', 'document_preparation', 'filing_fee', 'service_provider', 'itin_application', 'itin_caa_fee', 'itin_courier', 'other'] as const;
export type OpsExpenseCategory = (typeof OPS_EXPENSE_CATEGORIES)[number];
export const OPS_EXPENSE_LABELS: Record<OpsExpenseCategory, string> = {
  id_pack_mailing: 'ID pack mailing',
  letterstream: 'LetterStream charges',
  document_preparation: 'Approved document preparation',
  filing_fee: 'Filing expenses',
  service_provider: 'Approved service-provider charges',
  itin_application: 'ITIN application (W-7 / IRS)',
  itin_caa_fee: 'ITIN Certifying Acceptance Agent fee',
  itin_courier: 'ITIN courier / certified copies',
  other: 'Other authorized client expense',
};
/** Finance must pay an approved operational request within this many hours of the approval (elapsed time). */
export const OPS_PAYMENT_SLA_HOURS = 12;
/** Escalation ladder (hours after approval while unpaid). */
export const OPS_PAYMENT_ESCALATIONS = [
  { hours: 6, level: 1, notify: ['finance'] },
  { hours: 9, level: 2, notify: ['finance', 'supervisor'] },
  { hours: 11, level: 3, notify: ['finance', 'supervisor', 'owner'] },
  { hours: 12, level: 4, notify: ['supervisor', 'owner'], breach: true },
] as const;

// ---------- Follow-ups ----------
export const FOLLOW_UP_KINDS = [
  'llc_filing_pending',
  'state_approval_delayed',
  'ein_pending',
  'itin_pending',
  'irs_documents_missing',
  'id_pack_not_submitted',
  'mailing_failed',
  'bureau_follow_up',
  'credit_report_review',
  'bank_application_pending',
  'banker_callback',
  'amex_verification_pending',
  'card_delivery_pending',
  'client_no_response',
  'document_requested',
  'phase_review',
  'other',
] as const;
export type FollowUpKind = (typeof FOLLOW_UP_KINDS)[number];
export const FOLLOW_UP_LABELS: Record<FollowUpKind, string> = {
  llc_filing_pending: 'LLC filing pending',
  state_approval_delayed: 'State approval delayed',
  ein_pending: 'EIN application pending',
  itin_pending: 'ITIN application pending',
  irs_documents_missing: 'Missing IRS documents',
  id_pack_not_submitted: 'ID pack not submitted',
  mailing_failed: 'LetterStream mailing failed',
  bureau_follow_up: 'Bureau follow-up due',
  credit_report_review: 'Credit report review due',
  bank_application_pending: 'Bank application pending',
  banker_callback: 'Banker callback',
  amex_verification_pending: 'Amex verification pending',
  card_delivery_pending: 'Card delivery pending',
  client_no_response: 'Client has not responded',
  document_requested: 'Documents requested from client',
  phase_review: 'Phase review',
  other: 'Follow-up',
};
export const FOLLOW_UP_STATUSES = ['open', 'done', 'snoozed', 'cancelled'] as const;
export type FollowUpStatus = (typeof FOLLOW_UP_STATUSES)[number];
/** Who the follow-up is waiting on. Only `staff` follow-ups escalate as missed staff deadlines. */
export const RESPONSIBLE_PARTIES = ['staff', 'institution', 'client'] as const;
export type ResponsibleParty = (typeof RESPONSIBLE_PARTIES)[number];

// ---------- Roadmap templates ----------
export interface StepTemplate {
  key: string;
  title: string;
  /** Shown to the client in the portal progress. */
  clientVisible?: boolean;
  /** A document the client must supply for this step. */
  documentKey?: DocumentKey;
  /** Handled by the banker-call specialist (Paul) when one is assigned. */
  bankerCall?: boolean;
  /** Only when the enrollment includes this optional milestone. */
  requiresFlag?: 'includesLlc' | 'includesEin' | 'includesItin' | 'includesIdPack';
}
export interface PhaseTemplate {
  no: number;
  key: string;
  title: string;
  /** Default number of days from the phase start to its target completion (editable per client). */
  defaultWindowDays: number;
  /** Processing window shown to the client. */
  estimatedWindow: string;
  /** Phase starts immediately on enrollment (phases can overlap). */
  startsImmediately?: boolean;
  steps: StepTemplate[];
}

export const CREDIT_BUILDING_TEMPLATE: PhaseTemplate[] = [
  {
    no: 1,
    key: 'profile_id_pack',
    title: 'Profile & ID Pack',
    defaultWindowDays: 21,
    estimatedWindow: '2–3 weeks',
    startsImmediately: true,
    steps: [
      { key: 'client_information', title: 'Client information', clientVisible: true },
      { key: 'identity_documents', title: 'Identity documents', clientVisible: true, documentKey: 'gov_id_front' },
      { key: 'address_evidence', title: 'Address evidence', clientVisible: true, documentKey: 'proof_of_address_1' },
      { key: 'id_pack_preparation', title: 'ID pack preparation', clientVisible: true, requiresFlag: 'includesIdPack' },
      { key: 'letterstream_mailing', title: 'LetterStream mailing', clientVisible: true, requiresFlag: 'includesIdPack' },
      { key: 'bureau_delivery', title: 'Bureau delivery', clientVisible: true, requiresFlag: 'includesIdPack' },
      { key: 'bureau_follow_up', title: 'Bureau follow-up', clientVisible: true, requiresFlag: 'includesIdPack' },
    ],
  },
  {
    no: 2,
    key: 'credit_history',
    title: 'Credit History',
    defaultWindowDays: 30,
    estimatedWindow: '3–4 weeks',
    steps: [
      { key: 'credit_report_review', title: 'Credit report review', clientVisible: true, documentKey: 'credit_report' },
      { key: 'existing_credit_history', title: 'Existing credit history', clientVisible: true },
      { key: 'authorized_user_assessment', title: 'Authorized-user eligibility assessment', clientVisible: true },
      { key: 'reporting_checks', title: 'Reporting checks' },
      { key: 'scheduled_credit_review', title: 'Scheduled credit review', clientVisible: true },
    ],
  },
  {
    no: 3,
    key: 'secured_credit',
    title: 'Secured Credit',
    defaultWindowDays: 45,
    estimatedWindow: '4–6 weeks',
    steps: [
      { key: 'navy_federal_eligibility', title: 'Navy Federal eligibility', clientVisible: true },
      { key: 'account_opening', title: 'Account opening', clientVisible: true, bankerCall: true },
      { key: 'secured_card_application', title: 'Secured card application', clientVisible: true },
      { key: 'secured_loan_application', title: 'Secured loan application', clientVisible: true },
      { key: 'funding_confirmation', title: 'Funding confirmation', clientVisible: true, bankerCall: true },
      { key: 'payment_reminders', title: 'Payment reminders' },
    ],
  },
  {
    no: 4,
    key: 'unsecured_credit',
    title: 'Unsecured Credit',
    defaultWindowDays: 60,
    estimatedWindow: '1–2 months',
    steps: [
      { key: 'credit_readiness_review', title: 'Credit readiness review', clientVisible: true },
      { key: 'product_selection', title: 'Product selection', clientVisible: true },
      { key: 'application_preparation', title: 'Application preparation', clientVisible: true },
      { key: 'application_submission', title: 'Application submission', clientVisible: true },
      { key: 'decision_follow_up', title: 'Decision and follow-up', clientVisible: true, bankerCall: true },
    ],
  },
  {
    no: 5,
    key: 'banking_relationship',
    title: 'Banking Relationship',
    defaultWindowDays: 90,
    estimatedWindow: '2–3 months',
    steps: [
      { key: 'account_activity_review', title: 'Account activity review', clientVisible: true },
      { key: 'direct_deposit', title: 'Genuine direct deposit coordination', clientVisible: true },
      { key: 'banking_history', title: 'Banking history development', clientVisible: true },
      { key: 'payment_monitoring', title: 'Payment monitoring' },
    ],
  },
  {
    no: 6,
    key: 'graduation_review',
    title: 'Graduation Review',
    defaultWindowDays: 180,
    estimatedWindow: '6–12 months',
    steps: [
      { key: 'secured_account_age', title: 'Secured account age', clientVisible: true },
      { key: 'payment_history_review', title: 'Payment history review', clientVisible: true },
      { key: 'graduation_assessment', title: 'Graduation assessment', clientVisible: true, bankerCall: true },
      { key: 'credit_limit_review', title: 'Credit limit review', clientVisible: true, bankerCall: true },
    ],
  },
  {
    no: 7,
    key: 'business_banking_credit',
    title: 'Business Banking & Credit',
    defaultWindowDays: 120,
    estimatedWindow: '3–4 months',
    steps: [
      { key: 'llc_documentation', title: 'LLC documentation', clientVisible: true, requiresFlag: 'includesLlc' },
      { key: 'ein_duns', title: 'EIN and D-U-N-S', clientVisible: true, requiresFlag: 'includesEin' },
      { key: 'business_account_preparation', title: 'Business account preparation', clientVisible: true },
      { key: 'chase_amex_business_banking', title: 'Chase and Amex business banking', clientVisible: true, bankerCall: true },
      { key: 'starter_business_cards', title: 'Starter business cards', clientVisible: true },
      { key: 'business_credit_development', title: 'Business credit development', clientVisible: true },
      { key: 'higher_card_reviews', title: 'Higher-card eligibility reviews', clientVisible: true },
    ],
  },
];

export const GLOBAL_TRANSFER_TEMPLATE: PhaseTemplate[] = [
  {
    no: 1,
    key: 'eligibility_assessment',
    title: 'Eligibility Assessment',
    defaultWindowDays: 7,
    estimatedWindow: '3–7 days',
    startsImmediately: true,
    steps: [
      { key: 'foreign_amex_eligibility', title: 'Foreign Amex eligibility assessment', clientVisible: true, documentKey: 'foreign_amex_statement' },
      { key: 'application_classification', title: 'Personal or business application classification', clientVisible: true },
      { key: 'us_eligibility_verification', title: 'US eligibility verification', clientVisible: true, documentKey: 'us_address_proof' },
    ],
  },
  {
    no: 2,
    key: 'document_preparation',
    title: 'Document Preparation',
    defaultWindowDays: 10,
    estimatedWindow: '1–2 weeks',
    steps: [
      { key: 'document_checklist', title: 'Document checklist', clientVisible: true, documentKey: 'gov_id_front' },
      { key: 'document_review', title: 'Document review', clientVisible: true },
    ],
  },
  {
    no: 3,
    key: 'card_selection',
    title: 'Card Selection',
    defaultWindowDays: 5,
    estimatedWindow: '2–5 days',
    steps: [{ key: 'card_selection', title: 'Card selection', clientVisible: true }],
  },
  {
    no: 4,
    key: 'application_submission',
    title: 'Application Submission',
    defaultWindowDays: 5,
    estimatedWindow: '2–5 days',
    steps: [
      { key: 'application_preparation', title: 'Application preparation', clientVisible: true },
      { key: 'application_submitted', title: 'Application submitted', clientVisible: true },
    ],
  },
  {
    no: 5,
    key: 'verification_approval',
    title: 'Verification & Approval',
    defaultWindowDays: 21,
    estimatedWindow: '1–3 weeks',
    steps: [
      { key: 'verification_follow_up', title: 'Verification follow-ups', clientVisible: true },
      { key: 'banker_calls', title: 'Banker calls', bankerCall: true },
      { key: 'approval_tracking', title: 'Approval', clientVisible: true },
    ],
  },
  {
    no: 6,
    key: 'delivery_activation',
    title: 'Delivery & Activation',
    defaultWindowDays: 14,
    estimatedWindow: '1–2 weeks',
    steps: [
      { key: 'card_delivery', title: 'Card delivery', clientVisible: true },
      { key: 'card_activation', title: 'Card activation', clientVisible: true },
      { key: 'activation_follow_up', title: 'Activation follow-up', clientVisible: true },
    ],
  },
];

export const SERVICE_TEMPLATES: Record<ClientServiceType, PhaseTemplate[]> = {
  credit_building: CREDIT_BUILDING_TEMPLATE,
  global_transfer: GLOBAL_TRANSFER_TEMPLATE,
};

// ---------- Amex Global Transfer application tracker ----------
/** Result of the Amex Global Transfer application (independent of the roadmap phases). */
export const GT_OUTCOMES = ['pending', 'sent_to_verification', 'approved', 'declined', 'withdrawn'] as const;
export type GtOutcome = (typeof GT_OUTCOMES)[number];
export const GT_OUTCOME_LABELS: Record<GtOutcome, string> = {
  pending: 'Pending',
  sent_to_verification: 'Sent to verification',
  approved: 'Approved',
  declined: 'Declined',
  withdrawn: 'Withdrawn / cancelled',
};
/** Why the case manager asks the banker specialist (Paul) to call the Amex rep for a Global Transfer client. */
export const GT_CALL_PURPOSES = ['reconsideration', 'application_status', 'verification', 'credit_limit_increase', 'card_activation', 'other'] as const;
export type GtCallPurpose = (typeof GT_CALL_PURPOSES)[number];
export const GT_CALL_PURPOSE_LABELS: Record<GtCallPurpose, string> = {
  reconsideration: 'Reconsideration',
  application_status: 'Application status check',
  verification: 'Verification',
  credit_limit_increase: 'Credit limit increase',
  card_activation: 'Card activation',
  other: 'Other',
};
export const GT_CALL_URGENCIES = ['normal', 'today', 'urgent'] as const;
export type GtCallUrgency = (typeof GT_CALL_URGENCIES)[number];
export const GT_CALL_URGENCY_LABELS: Record<GtCallUrgency, string> = { normal: 'Normal (within 24h)', today: 'Today', urgent: 'Urgent' };
/** The institution recorded on Global Transfer call requests. */
export const AMEX_INSTITUTION = 'American Express';

/** Who has to act next on a Global Transfer client (derived, never stored). */
export const GT_TURNS = ['us', 'banker', 'client', 'institution'] as const;
export type GtTurn = (typeof GT_TURNS)[number];

// ---------- Bank applications (Credit Building) ----------
/** Banks Credit Building clients apply to: personal (Capital One, Alliant, Navy Federal) and business (Ramp, Amex). */
export const BANK_KEYS = ['capital_one', 'alliant_cu', 'navy_federal', 'ramp', 'amex_business_checking'] as const;
export type BankKey = (typeof BANK_KEYS)[number];
export const BANK_SIDES = ['personal', 'business'] as const;
export type BankSide = (typeof BANK_SIDES)[number];
export const BANK_LABELS: Record<BankKey, string> = {
  capital_one: 'Capital One',
  alliant_cu: 'Alliant Credit Union',
  navy_federal: 'Navy Federal Credit Union',
  ramp: 'Ramp',
  amex_business_checking: 'American Express Business Checking',
};
export const BANK_SHORT: Record<BankKey, string> = {
  capital_one: 'Capital One',
  alliant_cu: 'Alliant',
  navy_federal: 'Navy Federal',
  ramp: 'Ramp',
  amex_business_checking: 'Amex Business',
};
export const BANK_SIDE: Record<BankKey, BankSide> = { capital_one: 'personal', alliant_cu: 'personal', navy_federal: 'personal', ramp: 'business', amex_business_checking: 'business' };
/** Credit unions: membership with an eligibility basis (family member, DoD, veteran...). */
export const CREDIT_UNION_BANKS: readonly BankKey[] = ['alliant_cu', 'navy_federal'];
/** Case-insensitive (Postgres `~*`) pattern a bank call's institution must match to belong to the bank. */
export const BANK_INSTITUTION_PATTERNS: Record<BankKey, string> = {
  capital_one: 'capital ?one',
  alliant_cu: 'alliant',
  navy_federal: 'navy ?fed|nfcu',
  ramp: '\\mramp\\M',
  amex_business_checking: '(amex|american express).*business|business.*(amex|american express)',
};

/** Why the case manager asks the banker-call specialist to call a bank about a bank application. */
export const BANK_CALL_PURPOSES = ['application_status', 'reconsideration', 'verification', 'credit_limit_increase', 'secured_graduation', 'account_opening', 'other'] as const;
export type BankCallPurpose = (typeof BANK_CALL_PURPOSES)[number];
export const BANK_CALL_PURPOSE_LABELS: Record<BankCallPurpose, string> = {
  application_status: 'Application status check',
  reconsideration: 'Reconsideration',
  verification: 'Verification / documents',
  credit_limit_increase: 'Credit limit increase',
  secured_graduation: 'Secured-card graduation / deposit refund',
  account_opening: 'Account opening / membership',
  other: 'Other',
};

/** Membership (credit unions) or account status of a client at a bank. */
export const BANK_RELATIONSHIP_STATUSES = ['not_started', 'applying', 'active', 'declined', 'closed'] as const;
export type BankRelationshipStatus = (typeof BANK_RELATIONSHIP_STATUSES)[number];
export const BANK_RELATIONSHIP_STATUS_LABELS: Record<BankRelationshipStatus, string> = {
  not_started: 'Not started',
  applying: 'Applying',
  active: 'Member / account open',
  declined: 'Declined',
  closed: 'Closed',
};

export const BANK_PRODUCT_TYPES = [
  'membership_savings',
  'checking',
  'savings_checking',
  'platinum_secured_card',
  'quicksilver_secured_card',
  'secured_visa_card',
  'nrewards_secured_card',
  'credit_card',
  'share_secured_loan',
  'certificate_secured_loan',
  'corporate_card',
  'ramp_business_account',
  'business_checking',
  'other',
] as const;
export type BankProductType = (typeof BANK_PRODUCT_TYPES)[number];
export const BANK_PRODUCT_TYPE_LABELS: Record<BankProductType, string> = {
  membership_savings: 'Membership savings',
  checking: 'Checking',
  savings_checking: 'Savings / checking',
  platinum_secured_card: 'Platinum Secured card',
  quicksilver_secured_card: 'Quicksilver Secured card',
  secured_visa_card: 'Secured Visa card',
  nrewards_secured_card: 'nRewards Secured card',
  credit_card: 'Other card',
  share_secured_loan: 'Share-secured loan',
  certificate_secured_loan: 'Certificate-secured loan',
  corporate_card: 'Corporate card',
  ramp_business_account: 'Ramp business account',
  business_checking: 'Business checking account',
  other: 'Other',
};
/** Product types offered per bank (the first ones are what clients usually apply for). */
export const BANK_PRODUCTS: Record<BankKey, readonly BankProductType[]> = {
  capital_one: ['platinum_secured_card', 'quicksilver_secured_card', 'credit_card', 'savings_checking', 'other'],
  alliant_cu: ['membership_savings', 'checking', 'secured_visa_card', 'share_secured_loan', 'certificate_secured_loan', 'other'],
  navy_federal: ['membership_savings', 'checking', 'nrewards_secured_card', 'share_secured_loan', 'certificate_secured_loan', 'other'],
  ramp: ['corporate_card', 'ramp_business_account', 'other'],
  amex_business_checking: ['business_checking', 'other'],
};
/** Products that are loans (have a term in months). */
export const BANK_LOAN_TYPES: readonly BankProductType[] = ['share_secured_loan', 'certificate_secured_loan'];

export const BANK_PRODUCT_STATUSES = ['planned', 'submitted', 'pending', 'approved', 'declined', 'reconsideration', 'withdrawn', 'closed'] as const;
export type BankProductStatus = (typeof BANK_PRODUCT_STATUSES)[number];
export const BANK_PRODUCT_STATUS_LABELS: Record<BankProductStatus, string> = {
  planned: 'Planned',
  submitted: 'Submitted',
  pending: 'Pending',
  approved: 'Approved',
  declined: 'Declined',
  reconsideration: 'Reconsideration',
  withdrawn: 'Withdrawn',
  closed: 'Closed',
};
/** Applications still waiting on the bank's decision. */
export const BANK_PENDING_STATUSES: readonly BankProductStatus[] = ['submitted', 'pending', 'reconsideration'];
/** Products whose follow-up dates no longer matter. */
export const BANK_INACTIVE_STATUSES: readonly BankProductStatus[] = ['withdrawn', 'closed'];
/** The Access number, masked for lists: only the last 4 characters are shown. */
export const maskAccessNumber = (v: string | null | undefined) => (!v ? null : v.length > 4 ? `•••${v.slice(-4)}` : '••••');

// ---------- Client portal progress cards ----------
export interface PortalCard {
  key: string;
  title: string;
  /** Where the card's progress comes from. */
  source: 'llc' | 'ein' | 'itin' | 'id_pack' | 'phases' | 'steps';
  /** For `phases`: phase keys; for `steps`: "phaseKey.stepKey". */
  refs?: string[];
  requiresFlag?: 'includesLlc' | 'includesEin' | 'includesItin' | 'includesIdPack';
  responsibleTeam: string;
}

export const PORTAL_CARDS: Record<ClientServiceType, PortalCard[]> = {
  credit_building: [
    { key: 'llc', title: 'LLC Formation', source: 'llc', requiresFlag: 'includesLlc', responsibleTeam: 'Client Services' },
    { key: 'ein', title: 'EIN Application', source: 'ein', requiresFlag: 'includesEin', responsibleTeam: 'Client Services' },
    { key: 'itin', title: 'ITIN Application', source: 'itin', requiresFlag: 'includesItin', responsibleTeam: 'Client Services' },
    { key: 'credit_journey', title: 'Credit Building Journey', source: 'phases', refs: CREDIT_BUILDING_TEMPLATE.map((p) => p.key), responsibleTeam: 'Client Services' },
    {
      key: 'banking',
      title: 'Banking Applications',
      source: 'steps',
      refs: ['secured_credit.navy_federal_eligibility', 'secured_credit.account_opening', 'banking_relationship.account_activity_review', 'banking_relationship.direct_deposit', 'banking_relationship.banking_history', 'business_banking_credit.business_account_preparation', 'business_banking_credit.chase_amex_business_banking'],
      responsibleTeam: 'Client Services & Banker Calls',
    },
    {
      key: 'credit_cards',
      title: 'Credit Card Applications',
      source: 'steps',
      refs: ['secured_credit.secured_card_application', 'secured_credit.secured_loan_application', 'unsecured_credit.product_selection', 'unsecured_credit.application_submission', 'unsecured_credit.decision_follow_up', 'business_banking_credit.starter_business_cards', 'business_banking_credit.higher_card_reviews'],
      responsibleTeam: 'Client Services',
    },
    { key: 'id_pack', title: 'ID Pack Mailing', source: 'id_pack', requiresFlag: 'includesIdPack', responsibleTeam: 'Client Services' },
  ],
  global_transfer: [
    { key: 'eligibility', title: 'Eligibility Assessment', source: 'phases', refs: ['eligibility_assessment'], responsibleTeam: 'Client Services' },
    { key: 'documents', title: 'Document Preparation', source: 'phases', refs: ['document_preparation'], responsibleTeam: 'Client Services' },
    { key: 'card_selection', title: 'Card Selection', source: 'phases', refs: ['card_selection'], responsibleTeam: 'Client Services' },
    { key: 'submission', title: 'Application Submission', source: 'phases', refs: ['application_submission'], responsibleTeam: 'Client Services' },
    { key: 'verification', title: 'Verification', source: 'steps', refs: ['verification_approval.verification_follow_up', 'verification_approval.banker_calls'], responsibleTeam: 'Client Services & Banker Calls' },
    { key: 'approval_delivery', title: 'Approval and Delivery', source: 'steps', refs: ['verification_approval.approval_tracking', 'delivery_activation.card_delivery'], responsibleTeam: 'Client Services' },
    { key: 'activation', title: 'Activation and Follow-up', source: 'steps', refs: ['delivery_activation.card_activation', 'delivery_activation.activation_follow_up'], responsibleTeam: 'Client Services' },
  ],
};

/** Masks a taxpayer identification number: only the last four digits are ever stored or shown. */
export function last4(value: string | null | undefined): string | null {
  const d = (value ?? '').replace(/\D/g, '');
  return d.length >= 4 ? d.slice(-4) : null;
}
export const maskTin = (l4: string | null | undefined) => (l4 ? `•••-••-${l4}` : null);
